import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  ClipboardList,
  Cog,
  Factory,
  FileText,
  History,
  LayoutGrid,
  MessagesSquare,
  Package,
  Search,
  ShoppingCart,
  UsersRound,
  X,
  type LucideIcon
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useShownRoles } from "../../auth/useShownRoles";
import { HEADER_QK, headerApi, type GlobalSearchResponse } from "../../api/headerApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { screensFor } from "../hub/hubs";
import { motionPersonOf, Sheet, useIsPhone } from "../motion";
import { openViperLauncher } from "../viper/viperStore";
import { closeHeaderPanel, openHeaderPanel, registerInlineSearch, useHeaderPanel } from "./headerStore";
import { buildSearchOptions, canAskViper, permittedRecent, readRecent, recordRecent, type SearchOption } from "./searchModel";

// Release 1.5.0 global search (O-R52, Ctrl+K), ported from Summit Engineering's HeaderSearch and widened to the plant:
// work orders (600160, 600160-000, 600160|000), parts, customers, workcenters and machines, purchase orders, knowledge
// and procedures, and the person's own pages — grouped, recent items first, arrow keys move, Enter opens, "Ask VIPER"
// last. Each kind is authorised for the person on the server. ARIA combobox: focus stays in the input, the listbox
// announces the active option, a polite live region says how many results there are.

const ICONS: Record<string, LucideIcon> = {
  recent: History,
  workOrders: ClipboardList,
  parts: Package,
  customers: UsersRound,
  workcenters: Factory,
  machines: Cog,
  purchaseOrders: ShoppingCart,
  knowledge: BookOpen,
  procedures: FileText,
  pages: LayoutGrid,
  ask: MessagesSquare
};

export const SEARCH_DEBOUNCE_MS = 150;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

interface SearchPanelProps {
  variant: "inline" | "dialog";
  initialQuery?: string;
  onDone?: () => void;
  /** Test seam: server answer without the network. */
  fixture?: GlobalSearchResponse;
}

/** The search box and its grouped results. Inline in the header on wide screens; inside a dialog or phone sheet otherwise. */
export function SearchPanel({ variant, initialQuery = "", onDone, fixture }: SearchPanelProps) {
  const navigate = useNavigate();
  const roles = useShownRoles();
  const { principal } = useAuth();
  const person = motionPersonOf(principal);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(initialQuery);
  const [open, setOpen] = useState(variant === "dialog");
  const [active, setActive] = useState(-1);
  const [recentVersion, setRecentVersion] = useState(0);
  const [enterQueued, setEnterQueued] = useState(false);
  const debounced = useDebounced(text.trim(), SEARCH_DEBOUNCE_MS);
  const enabled = !fixture && debounced.length >= 2;
  const search = useApiQuery([...HEADER_QK.search(debounced)], () => headerApi.search(debounced), { enabled, staleTime: 30_000, gcTime: 60_000 });
  const server = fixture ?? (enabled ? search.data : undefined);

  const screens = useMemo(() => screensFor(roles), [roles]);
  const recent = useMemo(() => permittedRecent(readRecent(person), roles), [person, roles, recentVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const askViper = useMemo(() => canAskViper(roles), [roles]);
  const q = text.trim();
  const { groups, flat } = useMemo(
    () => buildSearchOptions({ query: q, recent, server, screens, canAskViper: askViper }),
    [q, recent, server, screens, askViper]
  );
  const showList = open && (flat.length > 0 || q.length >= 2);
  const loading = enabled && search.isFetching && !search.data;

  useEffect(() => setActive(-1), [debounced]);

  useEffect(() => {
    if (variant !== "inline") return;
    registerInlineSearch(() => {
      const el = inputRef.current;
      if (!el || el.offsetParent === null) return false;
      el.focus();
      el.select();
      setOpen(true);
      return true;
    });
    return () => registerInlineSearch(null);
  }, [variant]);

  useEffect(() => {
    if (variant === "dialog") window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [variant]);

  const finish = useCallback(() => {
    setOpen(variant === "dialog");
    setActive(-1);
    setText("");
    if (variant === "inline") inputRef.current?.blur();
    onDone?.();
  }, [onDone, variant]);

  const go = (option: SearchOption | undefined) => {
    if (!option) return;
    if (option.kind === "ask") {
      openViperLauncher(q);
      finish();
      return;
    }
    if (!option.to) return;
    if (option.group !== "recent") recordRecent(person, { kind: option.kind, to: option.to, label: option.label, detail: option.detail ?? null });
    setRecentVersion((v) => v + 1);
    finish();
    navigate(option.to);
  };

  // An Enter pressed while the answer was on its way opens the best row as soon as it arrives.
  const fetching = enabled && search.isFetching;
  useEffect(() => {
    if (!enterQueued || q !== debounced || fetching) return;
    setEnterQueued(false);
    go(flat.find((o) => o.group !== "recent" && o.kind !== "ask") ?? flat[0]);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const last = flat.length - 1;
    if (e.key !== "Enter") setEnterQueued(false);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (flat.length === 0 ? -1 : a >= last ? 0 : a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (flat.length === 0 ? -1 : a <= 0 ? last : a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0) {
        go(flat[active]);
        return;
      }
      // Enter with no row chosen opens the best record or page. While the answer for what was typed is still on its
      // way it waits for it (never opening a stale row or falling through to "Ask VIPER").
      if (q !== debounced || (enabled && search.isFetching)) {
        setEnterQueued(true);
        return;
      }
      const best = flat.find((o) => o.group !== "recent" && o.kind !== "ask");
      go(best ?? flat[0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (variant === "dialog") {
        onDone?.();
      } else if (open && q) {
        setOpen(false);
        setActive(-1);
      } else {
        setText("");
        inputRef.current?.blur();
      }
    }
  };

  const total = flat.filter((o) => o.kind !== "ask").length;
  const activeId = showList && active >= 0 ? `${listId}-opt-${active}` : undefined;

  const list = showList && (
    <ul
      id={`${listId}-list`}
      role="listbox"
      aria-label="Search results"
      className={
        variant === "inline"
          ? "absolute left-0 right-0 top-9 z-40 max-h-[70vh] overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          : "max-h-[60vh] overflow-auto py-1 max-md:max-h-none max-md:flex-1"
      }
      data-testid="global-search-results"
    >
      {loading && flat.length === 0 && (
        <li role="presentation" className="px-3 py-2 text-xs text-slate-500">
          Looking…
        </li>
      )}
      {!loading && q.length >= 2 && total === 0 && (
        <li role="presentation" className="px-3 py-2 text-xs text-slate-500" data-testid="global-search-empty">
          Nothing you can open matches “{q}”.
        </li>
      )}
      {groups.map((g) => {
        const Icon = ICONS[g.id] ?? Search;
        return (
          <li key={g.id} role="presentation" data-group={g.id}>
            <p className="px-3 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500" aria-hidden="true">
              {g.label}
              {g.truncated ? " — more match; type more to narrow" : ""}
            </p>
            <ul role="group" aria-label={g.label}>
              {g.options.map((o) => {
                const index = flat.indexOf(o);
                const selected = index === active;
                return (
                  <li
                    key={o.key}
                    id={`${listId}-opt-${index}`}
                    role="option"
                    aria-selected={selected}
                    className={`mx-1 flex cursor-pointer items-start gap-2 rounded-md px-2.5 py-1.5 ${selected ? "bg-brand-50" : "hover:bg-slate-50"}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseMove={() => setActive(index)}
                    onClick={() => go(o)}
                    data-testid={`global-search-option-${o.key}`}
                  >
                    <Icon aria-hidden="true" className={`mt-0.5 h-3.5 w-3.5 flex-none ${o.kind === "ask" ? "text-brand-600" : "text-slate-500"}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm ${o.kind === "ask" ? "font-medium text-brand-700" : "text-slate-800"}`}>{o.label}</span>
                      {o.detail && <span className="block truncate text-[11px] text-slate-500">{o.detail}</span>}
                    </span>
                    {o.badge && <span className="flex-none rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] text-slate-600">{o.badge}</span>}
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={variant === "inline" ? "relative w-full" : "flex min-h-0 flex-1 flex-col"} data-testid={variant === "inline" ? "global-search" : "global-search-panel"}>
      <div className={variant === "inline" ? "relative" : "relative border-b border-slate-200 px-3 py-2"}>
        <label htmlFor={`${listId}-input`} className="sr-only">
          Search work orders, parts, customers, machines, purchase orders, knowledge and pages
        </label>
        <Search aria-hidden="true" className={`pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500 ${variant === "inline" ? "left-2.5" : "left-5"}`} />
        <input
          ref={inputRef}
          id={`${listId}-input`}
          type="text"
          role="combobox"
          aria-expanded={Boolean(showList)}
          aria-controls={`${listId}-list`}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          autoComplete="off"
          spellCheck={false}
          maxLength={100}
          placeholder="Search jobs, parts, customers…"
          className={`h-8 w-full rounded-md border border-slate-200 bg-slate-50 pl-8 text-sm text-slate-800 placeholder:text-slate-500 focus:border-brand-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 ${
            variant === "inline" ? "pr-14" : "h-10 pr-3"
          }`}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => {
            setRecentVersion((v) => v + 1);
            setOpen(true);
          }}
          onBlur={() => {
            if (variant === "inline") window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={onKeyDown}
          data-testid="global-search-input"
        />
        {variant === "inline" && (
          <kbd
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-slate-200 bg-white px-1 font-sans text-[10px] font-medium text-slate-500"
            aria-hidden="true"
          >
            Ctrl K
          </kbd>
        )}
      </div>
      {list}
      <div aria-live="polite" className="sr-only" data-testid="global-search-count">
        {q.length >= 2 && !loading ? `${total} result${total === 1 ? "" : "s"}` : ""}
      </div>
    </div>
  );
}

/** Below the widest screens: a header button (an icon on a phone) that opens the search dialog. */
export function SearchButton() {
  return (
    <button
      type="button"
      className="inline-flex h-8 flex-none items-center gap-1.5 rounded-md border border-slate-200 px-1.5 text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-900 md:px-2 2xl:hidden"
      onClick={() => openHeaderPanel("search")}
      aria-label="Search"
      aria-keyshortcuts="Control+K Meta+K"
      title="Search work orders, parts, customers, machines, purchase orders, knowledge and pages (Ctrl+K)"
      data-testid="global-search-button"
      data-tour="search"
    >
      <Search aria-hidden="true" className="h-3.5 w-3.5" />
      
      
    </button>
  );
}

/** The search dialog: centred on a desktop, the whole screen on a phone. */
export function SearchDialog() {
  const panel = useHeaderPanel();
  const phone = useIsPhone();
  if (panel !== "search") return null;
  if (phone) {
    return (
      <Sheet open onClose={closeHeaderPanel} title="Search" phoneFullScreen data-testid="global-search-sheet">
        <SearchPanel variant="dialog" onDone={closeHeaderPanel} />
      </Sheet>
    );
  }
  return (
    <div className="fixed inset-0 z-[85] flex items-start justify-center bg-slate-900/30 px-4 pt-[10vh] print:hidden" onMouseDown={closeHeaderPanel} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="global-search-dialog"
      >
        <div className="flex items-center justify-between px-3 pt-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Search</h2>
          <button type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close search" onClick={closeHeaderPanel}>
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <SearchPanel variant="dialog" onDone={closeHeaderPanel} />
        <p className="border-t border-slate-200 px-3 py-1.5 text-[11px] text-slate-500">↑ ↓ to move · Enter to open · Esc to close · Ctrl+P goes to a page</p>
      </div>
    </div>
  );
}
